import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrand } from "@/contexts/BrandContext";
import { usePublicAbout } from "@/hooks/usePublicAbout";
import { Mail, Phone, MapPin, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function AboutPage() {
  const { t, language } = useLanguage();
  const { brand, localizedName, localizedTagline } = useBrand();
  const { about, team, loading } = usePublicAbout();

  const loc = (en: string, ar: string) => (language === "ar" && ar ? ar : en);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-16">
          <div className="container mx-auto px-4 text-center">
            <div className="mb-4">
              {brand.logoType === "image" ? (
                <img src={brand.logo} alt={localizedName(language)} className="h-16 w-16 mx-auto object-contain" />
              ) : (
                <span className="text-5xl">{brand.logo}</span>
              )}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground">
              {localizedName(language)}
            </h1>
            <p className="text-muted-foreground mt-2 text-lg">{localizedTagline(language)}</p>
          </div>
        </section>

        <div className="container mx-auto px-4 py-12 space-y-16">
          {loading ? (
            <div className="space-y-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="rounded-xl border bg-card animate-pulse h-32" />
              ))}
            </div>
          ) : (
            <>
              {/* Story */}
              {about && (about.story || about.storyAr) && (
                <section>
                  <h2 className="font-heading text-2xl font-bold text-foreground mb-4">
                    {t("about.story")}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line max-w-3xl">
                    {loc(about.story, about.storyAr)}
                  </p>
                </section>
              )}

              {/* Mission */}
              {about && (about.mission || about.missionAr) && (
                <section>
                  <h2 className="font-heading text-2xl font-bold text-foreground mb-4">
                    {t("about.mission")}
                  </h2>
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 max-w-3xl">
                    <p className="text-foreground leading-relaxed whitespace-pre-line">
                      {loc(about.mission, about.missionAr)}
                    </p>
                  </div>
                </section>
              )}

              {/* Team */}
              {team.length > 0 && (
                <section>
                  <h2 className="font-heading text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                    <Users className="h-6 w-6 text-primary" />
                    {t("about.team")}
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                    {team.map((member) => (
                      <Card key={member.id} className="text-center">
                        <CardContent className="p-6">
                          <Avatar className="h-20 w-20 mx-auto mb-3">
                            {member.photoUrl && <AvatarImage src={member.photoUrl} alt={loc(member.name, member.nameAr)} />}
                            <AvatarFallback className="text-lg bg-primary/10 text-primary">
                              {loc(member.name, member.nameAr).charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <h3 className="font-heading font-bold text-foreground">
                            {loc(member.name, member.nameAr)}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {loc(member.role, member.roleAr)}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </section>
              )}

              {/* Contact */}
              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-6">
                  {t("about.contact")}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
                  {about?.contactPhone && (
                    <Card>
                      <CardContent className="p-5 flex items-center gap-3">
                        <Phone className="h-5 w-5 text-primary shrink-0" />
                        <a href={`tel:${about.contactPhone}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                          {about.contactPhone}
                        </a>
                      </CardContent>
                    </Card>
                  )}
                  {about?.contactEmail && (
                    <Card>
                      <CardContent className="p-5 flex items-center gap-3">
                        <Mail className="h-5 w-5 text-primary shrink-0" />
                        <a href={`mailto:${about.contactEmail}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                          {about.contactEmail}
                        </a>
                      </CardContent>
                    </Card>
                  )}
                  {(about?.contactAddress || about?.contactAddressAr) && (
                    <Card>
                      <CardContent className="p-5 flex items-center gap-3">
                        <MapPin className="h-5 w-5 text-primary shrink-0" />
                        <span className="text-sm text-muted-foreground">
                          {loc(about?.contactAddress || "", about?.contactAddressAr || "")}
                        </span>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Social Links */}
                {brand.socialLinks.length > 0 && (
                  <div className="mt-6 flex gap-3">
                    {brand.socialLinks.map((link) => (
                      <a
                        key={link.platform}
                        href={link.url}
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                      >
                        {link.platform === "twitter" ? "𝕏" : link.platform === "facebook" ? "📘" : "📸"}
                      </a>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
